import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { field } from '../numberFormat';
import { useToast } from '../context/ToastContext';
import WeightResult from '../components/WeightResult';

// Prepare an invoice and its delivery notes from an order (the director,
// 14-15 Oct 2026). Everything that can be is filled in -- the customer, the
// dates, the goods named and priced from the price list, the lorry -- for him
// to check and complete. Preview shows the PDF marked PREVIEW; Issue takes the
// number and keeps the invoice as issued for good.

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const CARD = '#1e2329';
const DARK = '#0f1117';
const MUTED = '#6b7280';
const TEXT = '#f0f2f5';
const AMBER = '#f59e0b';
const RED = '#fca5a5';

const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 9px', fontSize: '13px', color: TEXT, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };
const lab = { fontSize: '10px', fontWeight: '700', color: MUTED, marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' };
const btn = (primary) => ({
  background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL,
  border: `1px solid ${primary ? TEAL : 'rgba(20,184,166,0.6)'}`, borderRadius: '8px', padding: '8px 16px',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap',
});
const card = { background: CARD, border: `1px solid ${BORD}`, borderRadius: '12px', padding: '16px', marginBottom: '16px' };

// Written as on his invoices: "33 488", "€ 2 252,45".
const group = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const fmt = (n, d) => { if (n === null || n === undefined || n === '' || !Number.isFinite(Number(n))) return ''; const [i, f] = Math.abs(Number(n)).toFixed(d).split('.'); return (Number(n) < 0 ? '-' : '') + group(i) + (f ? ',' + f : ''); };
const money = (n) => `€ ${fmt(n, 2)}`;
const num = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(/s/g, '').replace(',', '.')));
const lineTotal = (l) => Math.round(Number(num(l.quantity) || 0) * Number(num(l.unit_price) || 0) * 100) / 100;
const addDays = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const pages = (n) => (n <= 7 ? 1 : n <= 19 ? 2 : null);

export default function InvoiceForm() {
  const goBack = useBack('/admin/orders');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useToast();
  const orderId = params.get('order');
  const [data, setData] = useState(null);      // { order, hints, already }
  const [inv, setInv] = useState(null);        // the draft being edited
  const [numberSays, setNumberSays] = useState(null);
  const [busy, setBusy] = useState(null);      // 'preview' | 'issue'
  const [confirming, setConfirming] = useState(false);
  const [warnings, setWarnings] = useState([]);   // to look at before saying yes, never a refusal
  const [error, setError] = useState(null);
  const [weights, setWeights] = useState(null);   // what the calculator worked out
  const [weighing, setWeighing] = useState(false);
  // The order's own number, there before the first render that has it -- the
  // customer is chosen while the order is still arriving.
  const orderNo = useRef('');

  useEffect(() => {
    if (!orderId) { setError('Open this from an order: Orders → the order → Invoice.'); return; }
    api.get(`/api/v1/invoices/prepare/${orderId}`)
      .then(r => {
        orderNo.current = r.data.order.order_number || '';
        setData({ order: r.data.order, hints: r.data.hints, already: r.data.already || [] });
        setInv(r.data.draft);
        weighOnOpen(r.data.draft);
        // Who it is invoiced to: the order's customer -- or, for an order not
        // linked to one, Sera (the director, 15 Oct 2026: "by default always
        // display Sera").
        api.get('/api/v1/customers').then(cr => {
          const list = Array.isArray(cr.data) ? cr.data : [];
          setCustomers(list);
          if (!r.data.draft.customer_id) {
            const sera = list.find(c => /^sera/i.test(c.name)) || list[0];
            if (sera) chooseCustomer(sera);
          }
        }).catch(() => {});
      })
      .catch(err => setError(err.response?.data?.error || 'The order could not be read just now.'));
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The invoice goes to this customer: its details as printed, and its payment days.
  const [customers, setCustomers] = useState([]);
  const [newCust, setNewCust] = useState(null);   // the "New customer" form while open
  function chooseCustomer(c) {
    const days = c.payment_days ?? 30;
    setInv(x => ({
      ...x, customer_id: c.id,
      // The order number with what this customer's invoices say after it --
      // unless he has typed his own (16 Oct 2026).
      order_reference: refFollowsCustomer(x, c),
      customer: { name: c.name, name_line2: c.name_line2 || null, address_lines: c.address_lines || [], tax_number: c.tax_number || null },
      due_date: x.invoice_date ? addDays(x.invoice_date, days) : x.due_date,
    }));
    setData(d => ({ ...d, hints: { ...d.hints, payment_days: days, no_customer: null } }));
  }
  // The Order number as the customer has it: the order's own number, then the
  // customer's text (077). Kept as typed once it is no longer one of those.
  function refFollowsCustomer(x, c) {
    const own = orderNo.current;
    const plain = [own, c.order_reference_note].filter(Boolean).join(' ');
    const anyDefault = (customers || []).some(o => x.order_reference === [own, o.order_reference_note].filter(Boolean).join(' '));
    return !x.order_reference || x.order_reference === own || anyDefault ? plain : x.order_reference;
  }
  async function saveNewCustomer() {
    try {
      const { data: c } = await api.post('/api/v1/customers', {
        name: newCust.name, name_line2: newCust.name_line2 || null, address_lines: newCust.address,
        tax_number: newCust.tax_number || null, payment_days: newCust.payment_days === '' ? 30 : Number(newCust.payment_days),
      });
      setCustomers(cs => [...cs, c].sort((a, b) => a.name.localeCompare(b.name)));
      chooseCustomer(c);
      setNewCust(null);
      toast.success(`${c.name} added, and this invoice goes to them`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'The customer could not be added.');
    }
  }

  const set = (k, v) => setInv(x => ({ ...x, [k]: v }));
  const setLine = (i, k, v) => setInv(x => ({ ...x, lines: x.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const removeLine = (i) => setInv(x => ({ ...x, lines: x.lines.filter((_, j) => j !== i) }));
  const addLine = (kind) => setInv(x => {
    const line = kind === 'transport'
      ? { kind: 'transport', description: 'Transport', quantity: 1, unit: 'LKW', unit_price: '', on_delivery_note: false }
      : { kind: 'extra', description: '', quantity: 1, unit: 'pcs', unit_price: '', on_delivery_note: false };
    const t = x.lines.findIndex(l => l.kind === 'transport');
    const lines = [...x.lines];
    if (kind === 'extra' && t >= 0) lines.splice(t, 0, line); else lines.push(line);
    return { ...x, lines };
  });

  // The due date is always the payment days (30) after the invoice date,
  // whichever way the invoice date was set (16 Oct 2026).
  const dueFrom = (invoiceDate) => (invoiceDate ? addDays(invoiceDate, data?.hints?.payment_days ?? 30) : '');
  function setDispatch(v) {
    setInv(x => {
      // The invoice date is the dispatch date unless he set another.
      const invoiceDate = x.invoice_date === x.dispatch_date || !x.invoice_date ? v : x.invoice_date;
      return { ...x, dispatch_date: v, invoice_date: invoiceDate, due_date: dueFrom(invoiceDate) };
    });
  }
  function setInvoiceDate(v) {
    setInv(x => ({ ...x, invoice_date: v, due_date: dueFrom(v) }));
  }

  async function checkNumber(v) {
    if (!v) { setNumberSays(null); return; }
    try { const { data: r } = await api.get('/api/v1/invoices/check-number', { params: { number: v } }); setNumberSays(r.refuse ? { bad: r.refuse } : r.note ? { note: r.note } : null); }
    catch { setNumberSays(null); }
  }

  const body = () => ({
    ...inv,
    gross_weight_kg: num(inv.gross_weight_kg), net_weight_kg: num(inv.net_weight_kg),
    euro_pallets: num(inv.euro_pallets), pallet_spaces: num(inv.pallet_spaces),
    lines: inv.lines.map(l => ({ ...l, quantity: num(l.quantity), unit_price: num(l.unit_price), pallet_spaces: num(l.pallet_spaces) })),
  });

  // A window for the PDF, opened at the click itself: one opened after a wait
  // is taken for a pop-up and blocked -- issuing 00020-2026 (16 Oct 2026) waited
  // for the issue first, and no PDF appeared.
  function pdfWindow() {
    const w = window.open('', '_blank');
    if (w) {
      try { w.document.title = 'Making the PDF…'; w.document.body.style.cssText = 'font:15px system-ui;padding:28px;color:#555'; w.document.body.textContent = 'Making the PDF…'; } catch { /* nothing to say it with */ }
    }
    return w;
  }

  async function openPdf(promise, label, w = pdfWindow(), fileName = 'invoice.pdf') {
    try {
      const r = await promise;
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (w) { w.location = url; return; }
      // No window allowed: the PDF is saved as a file instead.
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${fileName} saved to your downloads — the browser did not allow a new window.`, 8000);
    } catch (err) {
      if (w) w.close();
      let msg = `${label} could not be made.`;
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch { /* keep */ }
      toast.error(msg);
    }
  }

  // The goods lines that name a part or product, in order: what is weighed.
  const weighedOf = (lines) => lines.map((l, i) => ({ l, i })).filter(({ l }) => l.kind === 'goods' && (l.part_id || l.product_id));
  // A line sold by the kilogram counts its boxes from the pieces ordered, and
  // its net is the kg typed.
  const weightLines = (lines) => weighedOf(lines).map(({ l }) => (l.pieces != null
    ? { part_id: l.part_id || null, product_id: l.product_id || null, quantity: l.pieces, net_kg: num(l.quantity) }
    : { part_id: l.part_id || null, product_id: l.product_id || null, quantity: num(l.quantity) }));

  // `use`: the tape and foils this load was packed with, as picked in the
  // result; the catalogue's defaults the first time.
  async function workOutWeights(use = weights ? weights.use : undefined) {
    setWeighing(true);
    try {
      const { data: r } = await api.post('/api/v1/invoices/weights', { lines: weightLines(inv.lines), use });
      setWeights(r);
    } catch (err) {
      toast.error(err.response?.data?.error || 'The weights could not be worked out just now.');
    } finally {
      setWeighing(false);
    }
  }

  // Into the form: totals rounded to whole kilograms, each line's pallet
  // spaces for the delivery note. All still editable. `auto` (as the form
  // opens): only what is fully known -- a total with something missing is
  // left empty rather than filled with an "at least".
  function applyWeights(r, { auto = false } = {}) {
    // Rounded UP to the next 50 kg for the invoice and delivery note (the
    // director, 15 Oct 2026: 'better to be on the upper side than too low';
    // his own invoices were always on the 50s). The breakdown keeps the exact.
    const up50 = (n) => Math.ceil(n / 50) * 50;
    const pick = (v, known) => (v !== null && v !== undefined ? up50(v) : auto ? null : up50(known));
    setInv(x => {
      const w = weighedOf(x.lines);
      return {
        ...x,
        net_weight_kg: pick(r.net_kg, r.known.net_kg) || x.net_weight_kg,
        gross_weight_kg: pick(r.gross_kg, r.known.gross_kg) || x.gross_weight_kg,
        euro_pallets: (r.pallets ?? (auto ? null : r.known.pallets)) || x.euro_pallets,
        pallet_spaces: (r.pallet_spaces ?? (auto ? null : r.known.pallet_spaces)) || x.pallet_spaces,
        lines: x.lines.map((l, i) => {
          const k = w.findIndex(e => e.i === i);
          const s = k >= 0 ? r.lines[k]?.pallet_spaces : null;
          return k >= 0 && s !== null && s !== undefined ? { ...l, pallet_spaces: s } : l;
        }),
      };
    });
    if (!auto) toast.success('Weights and pallets filled in — check them before issuing');
  }

  // As the form opens, from the order and the packaging (the director, 15 Oct
  // 2026: "pre-populate the number of pallet spaces"): each line's pallet
  // spaces, and the totals that can be worked out in full; the breakdown is
  // shown underneath. A draft with no goods to weigh is left alone.
  async function weighOnOpen(draft) {
    const lines = weightLines(draft.lines);
    if (!lines.length) return;
    setWeighing(true);
    try {
      const { data: r } = await api.post('/api/v1/invoices/weights', { lines });
      setWeights(r);
      applyWeights(r, { auto: true });
    } catch { /* the button is still there to try again */ }
    finally { setWeighing(false); }
  }

  async function preview() {
    setBusy('preview');
    await openPdf(api.post('/api/v1/invoices/preview', body(), { responseType: 'blob' }), 'The preview');
    setBusy(null);
  }

  // Issue, step one: what is still missing, named, before "are you sure" --
  // the same checks the issue itself makes.
  async function askIssue() {
    setBusy('check');
    try {
      const { data: r } = await api.post('/api/v1/invoices/check', body());
      if (r.problems && r.problems.length) {
        setError(r.problems);
        toast.error(r.problems.length === 1 ? r.problems[0] : `${r.problems.length} things are missing — they are listed at the top.`, 7000);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(null);
        setWarnings(r.warnings || []);
        setConfirming(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not check the invoice just now.');
    } finally {
      setBusy(null);
    }
  }

  async function issue() {
    setBusy('issue');
    const w = pdfWindow();   // now, while it is still the click
    try {
      const { data: issued } = await api.post('/api/v1/invoices', body());
      setConfirming(false);
      toast.success(`Invoice ${issued.number} issued`);
      const od = issued.order_dispatch;
      if (od) toast.success(`${od.order}: dispatch date ${od.from ? 'moved to' : 'set to'} ${od.to.split('-').reverse().join('.')}, as on the invoice.`, 8000);
      await openPdf(api.get(`/api/v1/invoices/${issued.id}/pdf`, { responseType: 'blob' }), 'The PDF', w, `Invoice ${issued.number}.pdf`);
      navigate('/admin/invoices');
    } catch (err) {
      if (w) w.close();
      setConfirming(false);
      const p = err.response?.data?.problems;
      toast.error(p && p.length > 1 ? `${p[0]} (and ${p.length - 1} more — see the form)` : (err.response?.data?.error || 'The invoice could not be issued.'), 9000);
      if (p) setError(p);
    } finally {
      setBusy(null);
    }
  }

  const goods = useMemo(() => (inv ? inv.lines.filter(l => l.kind === 'goods') : []), [inv]);
  const total = inv ? inv.lines.reduce((s, l) => s + lineTotal(l), 0) : 0;
  const spacesFromLines = goods.reduce((s, l) => s + (num(l.pallet_spaces) || 0), 0);
  // One lorry takes 33 pallet spaces (16 Oct 2026): more is flagged, not refused.
  const LORRY = 33;
  const spacesNow = num(inv?.pallet_spaces) ?? spacesFromLines;
  const missingPrice = inv ? inv.lines.filter(l => num(l.unit_price) === null).length : 0;
  const invPages = inv ? pages(inv.lines.length) : null;
  const notePages = pages(goods.length);

  if (error && !inv) return <div style={{ padding: '30px', color: RED, fontFamily: 'system-ui' }}>{Array.isArray(error) ? error.join(' ') : error}</div>;
  if (!inv) return <div style={{ padding: '30px', color: MUTED, fontFamily: 'system-ui' }}>Preparing the invoice…</div>;

  const c = inv.customer || {};
  // A date left empty is marked red once Issue has said it is missing.
  const dateStyle = (k) => ({ ...inp, ...(Array.isArray(error) && !inv[k] ? { borderColor: RED } : {}) });
  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: '1180px', margin: '0 auto' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0, marginBottom: '8px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px', marginRight: '4px' }} />Back
      </button>
      <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px' }}>Invoice for {data.order.order_number || `order ${data.order.id}`}</h1>
      <p style={{ color: MUTED, fontSize: '13px', margin: '0 0 16px' }}>
        Check everything, preview it, then issue it. Once issued it is kept as it is — a correction goes by credit note.
      </p>

      {data.already.length > 0 && (
        <div style={{ ...card, borderColor: AMBER, color: AMBER, fontSize: '13px' }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px' }} />
          This order already has {data.already.map(a => `#${a.number} (${money(a.total)})`).join(', ')}. Issue another only for a second dispatch.
        </div>
      )}
      {data.hints.no_customer && <div style={{ ...card, borderColor: AMBER, color: AMBER, fontSize: '13px' }}>{data.hints.no_customer}</div>}
      {Array.isArray(error) && (
        <div style={{ ...card, borderColor: '#7f1d1d', color: RED, fontSize: '13px' }}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>Before it can be issued:</div>
          {error.map((p, i) => <div key={i}>· {p}</div>)}
        </div>
      )}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', color: TEAL }}>Invoice To</span>
            <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {customers.length > 1 && (
                <select style={{ ...inp, width: 'auto', padding: '4px 8px', fontSize: '12px' }} value={inv.customer_id || ''}
                  onChange={e => { const ch = customers.find(x => String(x.id) === e.target.value); if (ch) chooseCustomer(ch); }}>
                  {customers.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              )}
              {!newCust && (
                <button onClick={() => setNewCust({ name: '', name_line2: '', address: '', tax_number: '', payment_days: '30' })}
                  style={{ ...btn(false), padding: '3px 10px', fontSize: '11px' }}>
                  <i className="fa-solid fa-plus" style={{ fontSize: '9px', marginRight: '4px' }} />New customer
                </button>
              )}
            </span>
          </div>
          {newCust && (
            <div style={{ border: `1px solid ${TEAL}`, borderRadius: '10px', padding: '10px', marginBottom: '10px', display: 'grid', gap: '8px' }}>
              <div style={{ fontSize: '12px', color: MUTED }}>A new customer: saved to Admin → Customers, and this invoice goes to them.</div>
              <div><span style={lab}>Name (as printed)</span><input style={inp} autoFocus value={newCust.name} onChange={e => setNewCust(n => ({ ...n, name: e.target.value }))} /></div>
              <div><span style={lab}>Second line (optional)</span><input style={inp} value={newCust.name_line2} onChange={e => setNewCust(n => ({ ...n, name_line2: e.target.value }))} /></div>
              <div><span style={lab}>Address (one line per row)</span><textarea rows={2} style={{ ...inp, resize: 'vertical' }} value={newCust.address} onChange={e => setNewCust(n => ({ ...n, address: e.target.value }))} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: '8px' }}>
                <div><span style={lab}>Tax number</span><input style={inp} value={newCust.tax_number} onChange={e => setNewCust(n => ({ ...n, tax_number: e.target.value }))} /></div>
                <div><span style={lab}>Payment days</span><input style={inp} value={newCust.payment_days} onChange={e => setNewCust(n => ({ ...n, payment_days: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button style={{ ...btn(false), color: '#9ca3af', borderColor: '#374151' }} onClick={() => setNewCust(null)}>Cancel</button>
                <button style={btn(true)} disabled={!newCust.name.trim()} onClick={saveNewCustomer}>Add and use</button>
              </div>
            </div>
          )}
          <div style={{ fontSize: '15px', fontWeight: 700 }}>{c.name || '—'}</div>
          {c.name_line2 && <div style={{ fontSize: '12px', fontWeight: 700 }}>{c.name_line2}</div>}
          {(c.address_lines || []).map((l, i) => <div key={i} style={{ fontSize: '12px', color: '#d1d5db' }}>{l}</div>)}
          {c.tax_number && <div style={{ fontSize: '12px', color: '#d1d5db', marginTop: '3px' }}><b>TAX Number:</b> {c.tax_number}</div>}
          <div style={{ marginTop: '10px' }}>
            <span style={lab}>Order number (as printed, two lines at most)</span>
            <textarea rows={2} style={{ ...inp, resize: 'vertical' }} value={inv.order_reference || ''} onChange={e => set('order_reference', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', alignContent: 'start' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={lab}>Invoice number</span>
            <input style={{ ...inp, fontWeight: 700, color: TEAL }} value={inv.number} onChange={e => set('number', e.target.value)} onBlur={e => checkNumber(e.target.value)} />
            <div style={{ fontSize: '11px', marginTop: '3px', color: numberSays?.bad ? RED : numberSays?.note ? AMBER : MUTED }}>
              {numberSays?.bad || numberSays?.note || `The next free number is ${data.hints.suggested_number}; type over it if needed.`}
            </div>
          </div>
          <div><span style={lab} title="When you issue, the order gets this dispatch date too">Dispatch date — the day the lorry leaves</span><input type="date" style={dateStyle('dispatch_date')} value={inv.dispatch_date || ''} onChange={e => setDispatch(e.target.value)} /></div>
          <div><span style={lab}>Invoice date</span><input type="date" style={dateStyle('invoice_date')} value={inv.invoice_date || ''} onChange={e => setInvoiceDate(e.target.value)} /></div>
          <div><span style={lab}>Due date: {data.hints.payment_days} days after the invoice date</span><input type="date" style={dateStyle('due_date')} value={inv.due_date || ''} onChange={e => set('due_date', e.target.value)} /></div>
          {!inv.dispatch_date && <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: MUTED }}>Type the dispatch date: the invoice date follows it, and the due date is always {data.hints.payment_days} days after the invoice date.</div>}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>Lines</div>
            <div style={{ fontSize: '12px', color: MUTED }}>
              {inv.lines.length} line{inv.lines.length === 1 ? '' : 's'} · invoice {invPages ? `${invPages} page${invPages === 1 ? '' : 's'}` : <span style={{ color: RED }}>too many lines (19 at most)</span>}
              {' '}· delivery note {notePages ? `${notePages} page${notePages === 1 ? '' : 's'}, twice` : <span style={{ color: RED }}>too many lines</span>}
              {missingPrice > 0 && <span style={{ color: AMBER }}> · {missingPrice} without a price</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={btn(false)} onClick={() => addLine('extra')}><i className="fa-solid fa-plus" style={{ fontSize: '11px', marginRight: '5px' }} />Extra line</button>
            {!inv.lines.some(l => l.kind === 'transport') && <button style={btn(false)} onClick={() => addLine('transport')}><i className="fa-solid fa-truck" style={{ fontSize: '11px', marginRight: '5px' }} />Transport</button>}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '900px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: MUTED, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <th style={{ padding: '6px', width: '30px' }}>No.</th>
                <th style={{ padding: '6px', width: '86px' }}>Number</th>
                <th style={{ padding: '6px' }}>Description</th>
                <th style={{ padding: '6px', width: '96px', textAlign: 'right' }}>Quantity</th>
                <th style={{ padding: '6px', width: '64px' }}>Unit</th>
                <th style={{ padding: '6px', width: '100px', textAlign: 'right' }}>Unit price €</th>
                <th style={{ padding: '6px', width: '110px', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '6px', width: '76px', textAlign: 'right' }} title="Printed on the delivery note">Pallet spaces</th>
                <th style={{ padding: '6px', width: '34px' }} />
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) => {
                const noPrice = num(l.unit_price) === null;
                return (
                  <tr key={i} style={{ borderTop: `1px solid ${BORD}`, verticalAlign: 'top' }}>
                    <td style={{ padding: '8px 6px', color: TEAL, fontWeight: 700 }}>{String(i + 1).padStart(2, '0')}</td>
                    <td style={{ padding: '4px 6px' }}>
                      {l.kind === 'goods' ? <input style={inp} value={l.article_no || ''} onChange={e => setLine(i, 'article_no', e.target.value)} />
                        : <span style={{ fontSize: '11px', color: MUTED }}>{l.kind === 'transport' ? 'transport' : 'extra'}</span>}
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <textarea rows={l.kind === 'extra' ? 2 : 1} style={{ ...inp, resize: 'vertical', fontWeight: 600 }} value={l.description || ''}
                        placeholder={l.kind === 'extra' ? 'Minimum Quantity Surcharge\nfor 309991 and 309992' : ''}
                        onChange={e => setLine(i, 'description', e.target.value)} />
                      {l.special_price && <div style={{ fontSize: '11px', color: AMBER, marginTop: '2px' }}>Price typed on the order, not the price list</div>}
                      {l.weigh_note && <div style={{ fontSize: '11px', color: AMBER, marginTop: '2px' }}>{l.weigh_note}</div>}
                    </td>
                    <td style={{ padding: '4px 6px' }}><input style={{ ...inp, textAlign: 'right' }} value={field(l.quantity)} onChange={e => setLine(i, 'quantity', e.target.value)} /></td>
                    <td style={{ padding: '4px 6px' }}><input style={inp} value={l.unit || ''} onChange={e => setLine(i, 'unit', e.target.value)} /></td>
                    <td style={{ padding: '4px 6px' }}>
                      <input style={{ ...inp, textAlign: 'right', borderColor: noPrice ? AMBER : BORD }} value={field(l.unit_price)} placeholder="no price"
                        onChange={e => setLine(i, 'unit_price', e.target.value)} />
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{noPrice ? '—' : money(lineTotal(l))}</td>
                    <td style={{ padding: '4px 6px' }}>
                      {l.kind === 'goods' && <input style={{ ...inp, textAlign: 'right' }} value={field(l.pallet_spaces)} onChange={e => setLine(i, 'pallet_spaces', e.target.value)} />}
                      {/* The lorry's load on its own line (the director, 28 Oct 2026), read only:
                          the figures from Transport details below, changed there. */}
                      {l.kind === 'transport' && (
                        <div title="From Transport details below: change them there" style={{ textAlign: 'right', fontSize: '12px', lineHeight: 1.45, padding: '5px 2px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          <div><b style={{ color: spacesNow > LORRY ? AMBER : TEXT }}>{spacesNow ?? '—'}</b> <span style={{ color: MUTED, fontSize: '11px' }}>spaces</span></div>
                          <div><b>{num(inv.euro_pallets) ?? '—'}</b> <span style={{ color: MUTED, fontSize: '11px' }}>pallets</span></div>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px' }}>
                      <button title="Take this line off" onClick={() => removeLine(i)} style={{ background: 'none', border: `1px solid ${BORD}`, color: RED, borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer' }}>
                        <i className="fa-solid fa-xmark" style={{ fontSize: '11px' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '24px', marginTop: '12px', fontSize: '15px' }}>
          <span style={{ color: MUTED }}>Total</span>
          <span style={{ fontWeight: 800, color: TEAL, fontVariantNumeric: 'tabular-nums' }}>{money(total)}</span>
        </div>
      </div>

      <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px' }}>
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ fontSize: '15px', fontWeight: 700 }}>Transport details</span>
          <button style={btn(false)} disabled={weighing} onClick={() => workOutWeights()}>
            <i className="fa-solid fa-weight-hanging" style={{ fontSize: '11px', marginRight: '6px' }} />{weighing ? 'Working it out…' : weights ? 'Work them out again' : 'Work out weights and pallets'}
          </button>
        </div>
        {weights && (
          <div style={{ gridColumn: '1 / -1', fontSize: '12px', color: MUTED, marginTop: '-4px' }}>
            Filled in from each item's packaging when the invoice opened: each line's pallet spaces{weights.gaps.length ? ', and the totals that could be worked out in full' : ', the pallets and the weights'}. Check them before issuing; every figure can be changed. After changing a line, work them out again and press “Use these”.
          </div>
        )}
        {weights && (
          <div style={{ gridColumn: '1 / -1' }}>
            <WeightResult result={weights} onUse={applyWeights} onChoose={use => workOutWeights(use)} />
          </div>
        )}
        <div>
          <span style={lab}>Gross weight (kg)</span>
          <input style={inp} value={field(inv.gross_weight_kg)} onChange={e => set('gross_weight_kg', e.target.value)} />
          {weights && weights.gross_kg != null && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>exactly {fmt(weights.gross_kg, 1)} kg · rounded up to the next 50</div>}
        </div>
        <div>
          <span style={lab}>Net weight (kg)</span>
          <input style={inp} value={field(inv.net_weight_kg)} onChange={e => set('net_weight_kg', e.target.value)} />
          {weights && weights.net_kg != null && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>exactly {fmt(weights.net_kg, 1)} kg · rounded up to the next 50</div>}
          {/* The old hint, only while the net weight is still empty. */}
          {data.hints.net_weight_kg && !inv.net_weight_kg && (
            <button onClick={() => set('net_weight_kg', data.hints.net_weight_kg)} style={{ background: 'none', border: 'none', color: TEAL, fontSize: '11px', padding: '3px 0', cursor: 'pointer', textAlign: 'left' }}>
              About {fmt(data.hints.net_weight_kg, 0)} kg from part weights{data.hints.net_weight_complete ? '' : ' (some parts have no weight)'} — use it
            </button>
          )}
        </div>
        <div><span style={lab}>Euro pallets</span><input style={inp} value={field(inv.euro_pallets)} onChange={e => set('euro_pallets', e.target.value)} /></div>
        <div>
          <span style={lab}>Pallet spaces</span>
          <input style={{ ...inp, ...(spacesNow > LORRY ? { borderColor: AMBER } : {}) }} value={field(inv.pallet_spaces)} onChange={e => set('pallet_spaces', e.target.value)} />
          {spacesNow > LORRY && <div style={{ fontSize: '11px', color: AMBER, marginTop: '3px' }}>{spacesNow} spaces: one lorry takes {LORRY}.</div>}
          {spacesFromLines > 0 && num(inv.pallet_spaces) !== spacesFromLines && (
            <button onClick={() => set('pallet_spaces', spacesFromLines)} style={{ background: 'none', border: 'none', color: TEAL, fontSize: '11px', padding: '3px 0', cursor: 'pointer' }}>
              {spacesFromLines} from the lines — use it
            </button>
          )}
        </div>
        <div><span style={lab}>Goods code</span><input style={inp} value={inv.goods_code || ''} onChange={e => set('goods_code', e.target.value)} /></div>
        <div><span style={lab}>Means of transport</span><input style={inp} value={inv.transport_mode || ''} onChange={e => set('transport_mode', e.target.value)} /></div>
        <label style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', color: '#d1d5db', cursor: 'pointer' }}>
          <input type="checkbox" checked={inv.early_payment_note !== false} onChange={e => set('early_payment_note', e.target.checked)} />
          Print "2% discount applies to all products, excluding transport, for payments made within 21 days from the dispatch date."
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', marginBottom: '40px' }}>
        <button style={btn(false)} disabled={!!busy} onClick={preview}>
          <i className="fa-solid fa-eye" style={{ fontSize: '12px', marginRight: '6px' }} />{busy === 'preview' ? 'Making the preview…' : 'Preview PDF'}
        </button>
        <button style={{ ...btn(true), opacity: numberSays?.bad ? 0.5 : 1 }} disabled={!!busy || !!numberSays?.bad} onClick={askIssue}>
          <i className="fa-solid fa-file-invoice" style={{ fontSize: '12px', marginRight: '6px' }} />Issue invoice #{inv.number}
        </button>
      </div>

      {confirming && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setConfirming(false)}>
          <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: '14px', padding: '20px', width: '90%', maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Issue invoice #{inv.number}?</div>
            <div style={{ fontSize: '13px', color: '#d1d5db', lineHeight: 1.5, marginBottom: '14px' }}>
              To {c.name || 'the customer'}, {money(total)}. Once issued it is kept exactly as it is: it cannot be changed or deleted, and a mistake is corrected with a credit note.
              The PDF opens straight after.
            </div>
            {warnings.length > 0 && (
              <div style={{ border: `1px solid ${AMBER}66`, background: `${AMBER}12`, borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', fontSize: '12.5px', color: '#fde68a', lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, color: AMBER, marginBottom: '4px' }}>Look at {warnings.length === 1 ? 'this' : 'these'} first — you can still issue:</div>
                {warnings.map((w, i) => <div key={i}>· {w}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button style={{ ...btn(false), color: '#9ca3af', borderColor: '#374151' }} onClick={() => setConfirming(false)}>Not yet</button>
              <button style={btn(true)} disabled={busy === 'issue'} onClick={issue}>{busy === 'issue' ? 'Issuing…' : 'Issue it'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
